import { useState, useEffect, useMemo } from "react";
import {
  AlertTriangle,
  X,
  TrendingUp,
  TrendingDown,
  CloudLightning,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getAllSeasons, getCurrentSeason } from "@/lib/stormsQueries";

// Storm-season month labels: Sep=0 … Aug=11
const MONTHS = ["Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug"];

function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-24 bg-[#111827] border border-white/10" />
        ))}
      </div>
      <div className="h-64 bg-[#111827] border border-white/10" />
    </div>
  );
}

function MonthModal({ monthIndex, storms, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[#111827] border border-white/15 p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-[#F9FAFB]">
              {MONTHS[monthIndex]} Storms
            </h3>
            <p className="text-xs text-[#6B7280] mt-0.5">All seasons</p>
          </div>
          <button
            onClick={onClose}
            className="text-[#6B7280] hover:text-[#F9FAFB] transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {storms.length === 0 ? (
          <p className="text-[#6B7280] text-sm">No storms named in this month.</p>
        ) : (
          <ul className="space-y-2">
            {storms.map(({ name, season }) => (
              <li key={`${name}-${season}`} className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-cyan-400 shrink-0" />
                  <span className="text-cyan-400 font-medium">{name}</span>
                </div>
                <span className="text-xs text-[#6B7280]">{season}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function CustomBarTooltip({ active, payload, label }) {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#0d1117] border border-white/10 px-3 py-2 text-xs">
        <p className="text-[#F9FAFB] font-medium">{label}</p>
        <p className="text-cyan-400">
          {payload[0].value} storm{payload[0].value !== 1 ? "s" : ""}
        </p>
      </div>
    );
  }
  return null;
}


export default function StormsDashboard() {
  const [allSeasons, setAllSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeMonth, setActiveMonth] = useState(null);

  async function loadData() {
    try {
      setLoading(true);
      const seasons = await getAllSeasons();
      setAllSeasons(seasons);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentSeasonLabel = getCurrentSeason();

  const stats = useMemo(() => {
    if (!allSeasons.length) return null;
    const count = (s) => Number(s.storm_count) || 0;
    const totalStorms = allSeasons.reduce((acc, s) => acc + count(s), 0);
    const active = allSeasons.filter((s) => count(s) > 0);
    const mostActive = active.length
      ? active.reduce((best, s) => (count(s) > count(best) ? s : best), active[0])
      : null;
    const leastActive = active.length
      ? active.reduce((best, s) => (count(s) < count(best) ? s : best), active[0])
      : null;
    return { totalStorms, mostActive, leastActive };
  }, [allSeasons]);

  const currentSeason = useMemo(
    () => allSeasons.find((s) => s.season === currentSeasonLabel) ?? null,
    [allSeasons, currentSeasonLabel]
  );

  // Aggregate monthly counts and storm lists across all seasons
  const aggregateMonthly = useMemo(() => {
    const counts = Array(12).fill(0);
    const storms = Array.from({ length: 12 }, () => []);
    for (const s of allSeasons) {
      for (let i = 0; i < 12; i++) {
        counts[i] += s.monthly_counts?.[i] ?? 0;
        for (const name of s.monthly_storm_names?.[i] ?? []) {
          storms[i].push({ name, season: s.season });
        }
      }
    }
    return { counts, storms };
  }, [allSeasons]);

  const chartData = useMemo(
    () => MONTHS.map((label, idx) => ({ month: label, count: aggregateMonthly.counts[idx] })),
    [aggregateMonthly]
  );

  if (loading) return <LoadingSkeleton />;

  if (error) {
    return (
      <div className="border border-red-900/40 bg-[#1a0a0a] p-4 flex items-start gap-3 text-red-400">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
        <p className="text-sm">Failed to load dashboard: {error}</p>
      </div>
    );
  }

  if (!allSeasons.length) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3 text-[#6B7280]">
        <CloudLightning className="w-10 h-10 opacity-30" />
        <p className="text-sm">No storm data available yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current season card */}
      <div className="border border-white/10 bg-[#111827] p-5">
        <p className="text-xs uppercase tracking-widest text-[#6B7280] mb-2">
          Current Season — {currentSeasonLabel}
        </p>
        <p className="text-3xl font-bold text-cyan-400 mb-3">
          {currentSeason ? Number(currentSeason.storm_count) || 0 : 0}
          <span className="text-sm font-normal text-[#6B7280] ml-2">storms</span>
        </p>
        {currentSeason && currentSeason.storm_names && currentSeason.storm_names.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {currentSeason.storm_names.map((name) => (
              <span
                key={name}
                className="px-2 py-0.5 text-xs font-medium bg-white/5 text-[#9CA3AF] border border-white/10"
              >
                {name}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-[#6B7280]">No storms named this season yet.</p>
        )}
      </div>

      {/* Summary stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="border border-white/10 bg-[#111827] p-5">
          <p className="text-xs uppercase tracking-widest text-[#6B7280] mb-2">Total Storms</p>
          <p className="text-3xl font-bold text-cyan-400">{stats?.totalStorms ?? 0}</p>
          <p className="text-xs text-[#6B7280] mt-1">across {allSeasons.length} seasons</p>
        </div>
        <div className="border border-white/10 bg-[#111827] p-5">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-cyan-400" />
            <p className="text-xs uppercase tracking-widest text-[#6B7280]">Most Active Season</p>
          </div>
          {stats?.mostActive ? (
            <>
              <p className="text-3xl font-bold text-cyan-400">{stats.mostActive.storm_count}</p>
              <p className="text-xs text-[#6B7280] mt-1">{stats.mostActive.season} season</p>
            </>
          ) : (
            <p className="text-[#6B7280] text-sm">—</p>
          )}
        </div>
        <div className="border border-white/10 bg-[#111827] p-5">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-4 h-4 text-blue-400" />
            <p className="text-xs uppercase tracking-widest text-[#6B7280]">Least Active Season</p>
          </div>
          {stats?.leastActive ? (
            <>
              <p className="text-3xl font-bold text-blue-400">{stats.leastActive.storm_count}</p>
              <p className="text-xs text-[#6B7280] mt-1">{stats.leastActive.season} season</p>
            </>
          ) : (
            <p className="text-[#6B7280] text-sm">—</p>
          )}
        </div>
      </div>

      {/* Total Storms by Month chart */}
      <div className="border border-white/10 bg-[#111827] p-5">
        <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#F9FAFB] mb-1">Total Storms by Month</h3>
        <p className="text-xs text-[#6B7280] mb-4">
          Named storms per month across all seasons (Sep → Aug). Click a bar to see storms.
        </p>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart
            data={chartData}
            margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
            onClick={(e) => {
              if (e && e.activeTooltipIndex != null) {
                setActiveMonth(e.activeTooltipIndex);
              }
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
            <XAxis
              dataKey="month"
              tick={{ fill: "#6B7280", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: "#6B7280", fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip content={<CustomBarTooltip />} cursor={{ fill: "rgba(6,182,212,0.08)" }} />
            <Bar
              dataKey="count"
              fill="#06b6d4"
              radius={[0, 0, 0, 0]}
              style={{ cursor: "pointer" }}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Month detail modal */}
      {activeMonth !== null && (
        <MonthModal
          monthIndex={activeMonth}
          storms={aggregateMonthly.storms[activeMonth]}
          onClose={() => setActiveMonth(null)}
        />
      )}
    </div>
  );
}
